import { useQuery } from '@tanstack/react-query'
import { getProcurementPurchaseRequest } from 'api'
import { BoxIcon } from 'assets/icons/BoxIcon'
import { PaperIcon } from 'assets/icons/PaperIcon'
import { Alert, AlertType, TextWarning } from 'components/Alert/Alert'
import { Badge } from 'components/Badge/Badge'
import { Card, CardBody, CardHeader } from 'components/Card/Card'
import { JustSpinner } from 'components/Loading/Loading'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { EmptyList } from 'components/Table/EmptyList'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  ColorVariant,
  Flex,
  H1WithBack,
  OutlineButton,
  PageWrapper,
} from 'styling/GlobalStyles'
import { BigText, NormalText } from 'styling/TypographyV2'
import { ProcurementPurchaseRequestQuoteResponse, PurchaseRequestItemType } from 'types/procurement'
import { AddOfferModal } from './Tabs/Components/AddOfferModal'
import { AuditLogs } from './Tabs/Components/AuditLogs'
import { DetailItem } from './Tabs/Components/DetailItem'
import { PurchaseRequestCard } from './Tabs/Components/PurchaseRequestCard'
import { SelectWinningOfferModal } from './Tabs/Components/SelectWinningOfferModal'
import { SupplierItem } from './Tabs/Components/SupplierItem'
import { WorkflowProgressItem } from './Tabs/Components/WorkflowProgressItem'

export const ProcurementDetails = () => {
  const { id } = useParams() as { id: string }
  const { data, isLoading } = useQuery({
    queryKey: ['procurement-details', id],
    queryFn: () => getProcurementPurchaseRequest(id),
  })
  const [selectedOffer, setSelectedOffer] =
    useState<ProcurementPurchaseRequestQuoteResponse | null>(null)
  const [addOffer, setAddOffer] = useState(false)
  const freeTextItems =
    data?.items.filter((item) => !item.isResolved && item.type === PurchaseRequestItemType.Text) ||
    []
  const catalogItems =
    data?.items.filter((item) => item.isResolved || item.type === PurchaseRequestItemType.Item) ||
    []

  if (isLoading)
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <JustSpinner />
      </div>
    )
  if (!data) return <NoData />
  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <H1WithBack title='Procurement Details' arrow />
        <Flex column gap='1rem' mt='20'>
          <PurchaseRequestCard purchaseRequest={data} />
          <Alert
            title='Approval blocked: 1 unresolved item(s) must be mapped before approval'
            description='All free-text lines must be resolved to valid SKUs or service categories before this PR can proceed.'
            colorVariant={ColorVariant.PRIMARY}
            type={AlertType.WARNING}
            dismissKey='procurement-details-alert'
          />
          <Grid>
            <Flex column gap='1rem'>
              {/* Workflow Progress */}
              <Card padding='0.5rem 1rem'>
                <CardHeader>
                  <BigText semibold colorVariant={ColorVariant.BASE}>
                    Workflow Progress
                  </BigText>
                </CardHeader>
                <CardBody>
                  <Flex column>
                    {data.workflowSteps.length > 0 ? (
                      data.workflowSteps.map((step, index) => (
                        <WorkflowProgressItem
                          key={step.id}
                          step={step}
                          currentStep={data.currentWorkflowStep}
                          lastStep={index === data.workflowSteps.length - 1}
                        />
                      ))
                    ) : (
                      <EmptyList
                        title='No workflow steps found'
                        description='No workflow steps found'
                      />
                    )}
                  </Flex>
                </CardBody>
              </Card>

              <Flex column gap='1rem'>
                {/* Suppliers offers */}
                <Card padding='0.5rem 1rem'>
                  <CardHeader>
                    <Flex between center>
                      <BigText semibold colorVariant={ColorVariant.BASE}>
                        Suppliers offers
                      </BigText>
                      {data.canQuoteOrChooseWinningQuote && (
                        <OutlineButton size='small' onClick={() => setAddOffer(true)}>
                          Add Offer
                        </OutlineButton>
                      )}
                    </Flex>
                  </CardHeader>
                  <CardBody padding='1rem'>
                    <Flex column gap='0.5rem'>
                      {data.quotes.length > 0 ? (
                        data.quotes.map((quote) => (
                          <SupplierItem
                            key={quote.id}
                            supplier={quote}
                            isWinningOffer={data.selectedQuote?.id === quote.id}
                            setSelectedOffer={data.selectedQuote?.id ? undefined : setSelectedOffer}
                          />
                        ))
                      ) : (
                        <EmptyList title='No quotes found' description='No quotes found' />
                      )}
                    </Flex>
                  </CardBody>
                </Card>

                {/* Line Iitems */}
                <Card padding='0.5rem 1rem'>
                  <CardHeader>
                    <BigText semibold colorVariant={ColorVariant.BASE}>
                      Line Iitems
                    </BigText>
                  </CardHeader>
                  <CardBody>
                    {catalogItems.length > 0 && (
                      <Flex column mb='12' gap='0.5rem'>
                        <Flex between center mb='4'>
                          <Flex center gap='0.5rem'>
                            <BoxIcon />
                            <NormalText colorVariant={ColorVariant.SECONDARY} uppercase semibold>
                              CATALOG ITEMS
                            </NormalText>
                            <Badge text="SKU" green />
                            <Badge text={String(catalogItems.length)} />
                          </Flex>
                        </Flex>

                        {catalogItems.length > 0 ? (
                          catalogItems.map((item) => (
                            <DetailItem
                              key={item.id}
                              item={item}
                              winningOffer={data.selectedQuote}
                            />
                          ))
                        ) : (
                          <EmptyList
                            title='No line items found'
                            description='No line items found'
                          />
                        )}
                      </Flex>
                    )}
                    {freeTextItems.length > 0 && (
                      <Flex column gap='0.5rem'>
                        <Flex between center mb='4'>
                          <Flex center gap='0.5rem'>
                            <PaperIcon />
                            <NormalText colorVariant={ColorVariant.SECONDARY} uppercase semibold>
                              FREE TEXT ITEMS
                            </NormalText>
                            <Badge text={String(freeTextItems.length)} orange />
                          </Flex>
                          <TextWarning
                            title={`${freeTextItems.length} Unresolved`}
                            type={AlertType.WARNING}
                            colorVariant={ColorVariant.PRIMARY}
                          />
                        </Flex>
                        {freeTextItems.length > 0 ? (
                          freeTextItems.map((item) => (
                            <DetailItem
                              key={item.id}
                              item={item}
                              winningOffer={data.selectedQuote}
                            />
                          ))
                        ) : (
                          <EmptyList
                            title='No line items found'
                            description='No line items found'
                          />
                        )}
                      </Flex>
                    )}
                  </CardBody>
                </Card>
              </Flex>
            </Flex>
            {/* Audit Log */}
            <AuditLogs purchaseRequest={data} />
          </Grid>
        </Flex>
      </PageWrapper>
      {addOffer && <AddOfferModal id={id} items={data.items} onClose={() => setAddOffer(false)} />}
      {selectedOffer && (
        <SelectWinningOfferModal
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
          id={id}
        />
      )}
    </>
  )
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 1rem;
  width: 100%;
`
